/**
 * Copyright (c) Qwoach LLC 2026. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */
import CanvasGuidanceManager from "@/core/components/CanvasGuidance/CanvasGuidanceManager.js";

const GENOGRAM_ONBOARDING_STORAGE_KEY = "genogramOnboardingCompletedSteps"
const GENOGRAM_ONBOARDING_STEP_ID = "dragAddFromLeftToCanvas"
const NARROW_VIEWPORT_STEP_ID = "narrowViewportWarning"
const MIN_GENOGRAM_VIEWPORT_WIDTH_PX = 768
const PATTERN_EXPLORATION_STEP_ID = "patternExplorationMode"
const OVERLAP_DRAG_STEP_ID = "nodeOverlapDragTip"

export default class GenogramGuidanceManager extends CanvasGuidanceManager {

	constructor({notifyStateChanged, canShowOnboarding, canShowHelper}) {
		super({
			notifyStateChanged, canShowOnboarding,
			storageKey: GENOGRAM_ONBOARDING_STORAGE_KEY,
			stepId: GENOGRAM_ONBOARDING_STEP_ID,
			desktopMessage: "Drag & drop a symbol or template from the left to add it to the canvas.",
			touchMessage: "To add symbol, tap it on the left panel, then tap on canvas.",
		})
		this.canShowHelper = canShowHelper || (() => true)
		this.pendingTapAddItemName = ""
		this.pendingTapAddItemType = ""
		this.isPatternExplorationModeActive = false
		this.isOverlapDragTipPending = false
	}

	handleDragAddCompleted() {
		this.pendingTapAddItemName = ""
		this.pendingTapAddItemType = ""
		super.handleDragAddCompleted()
	}

	showTapToAddMessage(itemName, itemType = "symbol") {
		if(!itemName){
			return
		}

		this.clearDelayedOnboardingTimeout()
		this.pendingTapAddItemName = itemName
		this.pendingTapAddItemType = itemType || "symbol"
		this.syncState()
	}

	clearTapToAddMessage() {
		if(!this.pendingTapAddItemName){
			return
		}

		this.pendingTapAddItemName = ""
		this.pendingTapAddItemType = ""
		this.syncState()
	}

	setPatternExplorationModeActive(isActive) {
		if(this.isPatternExplorationModeActive === isActive){
			return
		}

		this.isPatternExplorationModeActive = isActive
		this.syncState()
	}

	handleNodeAddOverlapDetected() {
		if(this.isStepCompleted(OVERLAP_DRAG_STEP_ID)){
			return
		}

		this.isOverlapDragTipPending = true
		this.syncState()
	}

	handleNodeDragLearned() {
		if(!this.isOverlapDragTipPending){
			return
		}

		this.isOverlapDragTipPending = false
		this.completeStep(OVERLAP_DRAG_STEP_ID)
	}

	getActiveStep() {
		let activeStep = null
		activeStep = this.getNarrowViewportStep()
		if(this.canShowHelper()){
			activeStep = activeStep || this.getTapToAddStep()
			activeStep = activeStep || this.getOverlapDragStep()
		}
		if(this.canShowOnboarding() && !activeStep){
			activeStep = this.getDragAddStep()
		}
		activeStep = activeStep || this.getPatternExplorationStep()
		return activeStep
	}

	getNarrowViewportStep() {
		let step = null
		if(this.isNarrowGenogramViewport()){
			step = {
				id: NARROW_VIEWPORT_STEP_ID,
				message: "Switch to landscape or open on your computer for better experience.",
				placement: "bottom-center",
			}
		}
		return step
	}

	getTapToAddStep() {
		let step = null
		if(this.pendingTapAddItemName){
			let cancelHint = this.isTapDevice() ? "" : "\nEsc to cancel"
			step = {
				id: "tapAddSelectedItem",
				message: `Tap on the canvas to add ${this.pendingTapAddItemName} ${this.pendingTapAddItemType || "symbol"}.${cancelHint}`,
				placement: "bottom-center",
			}
		}
		return step
	}

	getOverlapDragStep() {
		let step = null
		if(this.isOverlapDragTipPending){
			step = {
				id: OVERLAP_DRAG_STEP_ID,
				message: "You can drag and drop symbols to reposition them.",
				placement: "bottom-center",
			}
		}
		return step
	}

	getPatternExplorationStep() {
		let step = null
		if(this.isPatternExplorationModeActive){
			step = {
				id: PATTERN_EXPLORATION_STEP_ID,
				message: "You are in pattern exploration mode. Select elements from the legend.\nClick \"Clear selection\" in the legend to exit this mode.",
				placement: "top-center",
			}
		}
		return step
	}

	isNarrowGenogramViewport() {
		let isNarrow = false
		if(typeof window !== "undefined"){
			if(window?.qwGenogramEnv?.isNarrowGenogramViewport != null){
				isNarrow = window.qwGenogramEnv.isNarrowGenogramViewport === true
			} else {
				isNarrow = (window.innerWidth || 0) < MIN_GENOGRAM_VIEWPORT_WIDTH_PX
			}
		}
		return isNarrow
	}

	isTapDevice() {
		return window?.qwGenogramEnv?.isTapDevice === true
	}
}
